import { SidebarHeader, SidebarLayout, Spinner } from '@scale/scaleui';
import React, { useEffect } from 'react';
import { BrowserRouter, useLocation } from 'react-router-dom';
import { useToggle } from 'react-use-v13';

import { QueryClient, QueryClientProvider } from '@tanstack/react-query-v4';
import ScaleHead from 'design-system/Head';

import queryString from 'query-string';
import { ExpertManagementContent } from 'react-scale/components/ExpertManagement/ExpertManagementContent';
import { ExpertManagementNavigation } from 'react-scale/components/ExpertManagement/ExpertManagementNavigation';
import { useFetchLoggedInUser } from 'react-scale/components/ExpertManagement/hooks/queries/useFetchLoggedInUser';
import { useFetchPodTypes } from 'react-scale/components/ExpertManagement/hooks/queries/useFetchPodTypes';
import { useFetchProject } from 'react-scale/components/ExpertManagement/hooks/queries/useFetchProject';
import { useFetchTeamLeadPermissions } from 'react-scale/components/ExpertManagement/hooks/queries/useFetchTeamLeadPermissions';
import { WorkerManagerDashboardHeader } from './WorkerManagerDashboardHeader';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
    },
  },
});

const ExpertManagementPage: React.FC = () => {
  const { isLoading: userLoading, isSuccess } = useFetchLoggedInUser();
  const { isLoading: podTypesLoading } = useFetchPodTypes();
  const { isLoading: permissionsLoading, refetch } = useFetchTeamLeadPermissions();
  const location = useLocation();
  const { project } = queryString.parse(location.search);
  const { isLoading: projectLoading } = useFetchProject(project as string);

  useEffect(() => {
    if (isSuccess) refetch();
  }, [isSuccess, refetch]);

  const [sidebarOpen, toggleSidebarOpen] = useToggle(false);

  const loading = userLoading || podTypesLoading || permissionsLoading || projectLoading;

  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter basename="/corp/expert-management">
        <div className="scaleui scale-theme scale-tailwind bg-neutral-50">
          <ScaleHead title="Team Lead Dashboard" />
          <SidebarLayout
            sidebarOpen={sidebarOpen}
            sidebar={
              <div className="flex flex-col flex-grow mt-4 overflow-y-auto bg-neutral-0">
                <SidebarHeader toggleSidebar={toggleSidebarOpen} sidebarOpen={sidebarOpen} />
                <div className="flex flex-col justify-between h-full">
                  <ExpertManagementNavigation sidebarOpen={sidebarOpen} />
                </div>
              </div>
            }
            header={<WorkerManagerDashboardHeader />}
            content={
              loading ? <Spinner className="m-4" size={'lg'} /> : <ExpertManagementContent />
            }
          />
        </div>
      </BrowserRouter>
    </QueryClientProvider>
  );
};

export default ExpertManagementPage;
